import { useState } from "react";
import { ChevronDown, Tag } from "lucide-react";
import type { PlanLeg } from "@/lib/types";
import LegFaresPanel from "./LegFaresPanel";
import DateFlexPanel from "./DateFlexPanel";
import PriceHistoryStrip from "./PriceHistoryStrip";

interface DayFaresDisclosureProps {
  tripId: string;
  /** The plan legs whose flights depart on this day. Empty renders nothing. */
  legs: PlanLeg[];
}

/**
 * Every fare affordance for one day, behind one closed door.
 *
 * The price history, the "moving a day" flex panel and the fare matrix are all
 * useful — during a fare SEARCH. On an itinerary they are noise, which is the
 * complaint this view exists to answer. So they are not deleted and not
 * rewritten: they are reused verbatim, one click away, and hidden entirely from
 * the printed page. An itinerary you carry on a trip has no use for a fare grid.
 */
export default function DayFaresDisclosure({
  tripId,
  legs,
}: DayFaresDisclosureProps) {
  const [open, setOpen] = useState(false);
  if (legs.length === 0) return null;

  return (
    <div className="print:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/40 hover:text-foreground motion-reduce:transition-none"
      >
        <Tag className="h-3.5 w-3.5" aria-hidden="true" />
        Fares &amp; alternatives
        <ChevronDown
          className={
            "h-3.5 w-3.5 transition-transform motion-reduce:transition-none " +
            (open ? "rotate-180" : "")
          }
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          {legs.map((leg) => (
            <div key={leg.routeId} className="space-y-2">
              {legs.length > 1 && (
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="font-mono">{leg.origin}</span> →{" "}
                  <span className="font-mono">{leg.destination}</span>
                </p>
              )}
              <PriceHistoryStrip history={leg.history} compact />
              <DateFlexPanel flex={leg.dateFlex} />
              <LegFaresPanel tripId={tripId} leg={leg} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
